import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerService } from '../../services/customerService';
import { formatCurrency } from '../../utils/formatters';

const Skeleton: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <div
    style={{
      background: 'linear-gradient(90deg, rgba(239,232,213,0.6) 25%, rgba(92,78,51,0.2) 50%, rgba(239,232,213,0.6) 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.8s infinite',
      borderRadius: 3,
      ...style,
    }}
  />
);

export const CustomerStats: React.FC = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['customer-stats'],
    queryFn: customerService.getCustomerStats
  });

  if (isLoading) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 6, padding: 20 }}>
            <Skeleton style={{ height: 9, width: '60%', marginBottom: 12 }} />
            <Skeleton style={{ height: 28, width: '80%' }} />
          </div>
        ))}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const cards = [
    {
      title: 'Total Customers',
      value: (stats.totalCustomers ?? 0).toLocaleString(),
      accent: 'rgba(90,140,201,0.3)',
      iconColor: '#5a8cc9',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      title: 'Active Customers',
      value: (stats.activeCustomers ?? 0).toLocaleString(),
      accent: 'rgba(90,201,140,0.3)',
      iconColor: '#5ac98c',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Inactive Customers',
      value: (stats.inactiveCustomers ?? 0).toLocaleString(),
      accent: 'rgba(201,90,90,0.3)',
      iconColor: '#c95a5a',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'Total Outstanding',
      value: formatCurrency(stats.totalOutstanding ?? 0),
      accent: 'rgba(217,119,6,0.35)',
      iconColor: '#d97706',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
      {cards.map((card, i) => (
        <div
          key={i}
          style={{
            background: '#ffffff',
            border: `1px solid ${card.accent}`,
            borderRadius: 6,
            padding: 20,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: 1,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              background: `linear-gradient(90deg, transparent, ${card.accent}, transparent)`,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 9,
                fontWeight: 500,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: '#6b6a5c',
              }}
            >
              {card.title}
            </div>
            <div style={{ color: card.iconColor, opacity: 0.8 }}>{card.icon}</div>
          </div>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 28,
              fontWeight: 300,
              lineHeight: 1,
              background: 'linear-gradient(135deg, #d97706, #fbbf24)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {card.value}
          </div>
        </div>
      ))}
    </div>
  );
};
